// frontend/src/pages/competitions/IndividualCompPage.jsx

import { useState, useEffect } from "react";
import { useParams, useLocation, useSearchParams, Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import SubmissionsGrid from "@/components/individualcomp/SubmissionsGrid";
import CompetitionDetails from "@/components/individualcomp/CompetitionDetails";
import IndividualCompLayout from "@/layouts/IndividualCompLayout";

import { useCompetition } from "@/contexts/CompetitionContext";
import { useModal } from "@/contexts/ModalContext";

import {
  fetchCompetitionDetails,
  fetchSubmissions,
} from "@/services/apiService";

// ===============================
// Individual Competition Page
// ===============================
const IndividualCompPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Global competition context
  const { competition, setCompetition } = useCompetition();

  // Page-local state
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal controller
  const { openModal, closeModal, modalType, modalProps } = useModal();

  // -----------------------------------------
  // Fetch competition + submissions
  // -----------------------------------------
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const fetchedCompetition = await fetchCompetitionDetails(id);
        setCompetition(fetchedCompetition);

        const fetchedSubmissions = await fetchSubmissions({
          comp_id: id,
        });

        setSubmissions(fetchedSubmissions);
      } catch (err) {
        console.error("❌ Failed to fetch competition or submissions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, setCompetition]);

  // -----------------------------------------
  // Submission list mutators (page-local)
  // -----------------------------------------
  const updateSubmissions = (newSubmission) => {
    if (newSubmission.approved) {
      setSubmissions((prev) => [...prev, newSubmission]);
    }
  };

  const handleDeleteSubmission = (submissionId) => {
    setSubmissions((prev) =>
      prev.filter((submission) => submission.id !== submissionId)
    );
  };

  // -----------------------------------------
  // 🔗 Deep-link: open a submission modal from ?submission=<id>
  //
  // Runs once submissions are loaded. If the URL carries a submission param
  // that matches a loaded submission, the modal opens automatically — this
  // is what makes a shared submission link land directly on that submission.
  // -----------------------------------------
  useEffect(() => {
    if (loading || submissions.length === 0) return;

    const submissionParam = searchParams.get("submission");
    if (!submissionParam) return;

    const targetId = Number(submissionParam);
    const target = submissions.find((s) => s.id === targetId);
    if (!target) return;

    openModal("SUBMISSION_PREVIEW", {
      submission: target,
      user: undefined,                // resolved inside the modal via auth context
      onDelete: handleDeleteSubmission,
      onEdit: (editSubmission) =>
        openModal("EDIT_SUBMISSION", {
          submission: editSubmission || target,
          updateSubmissions,
        }),
      onVoteUpdate: () => {},
    });
    // Only run on initial load / when submissions first populate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, submissions]);

  // -----------------------------------------
  // 🔗 Sync the open submission to the URL (?submission=<id>).
  //
  // This lives HERE in the page — not in SubmissionModal — because the modal
  // is rendered by ModalManager at the provider level, ABOVE the <Router>.
  // Router hooks (useSearchParams) only work inside the router, and the page
  // is inside it. The page already knows when the submission modal is open
  // via modalType/modalProps, so it owns URL sync in both directions.
  //
  // Open  → write ?submission=<id>  (makes the Share link land on this submission)
  // Close → strip the param          (handled in the effect below)
  // -----------------------------------------
  useEffect(() => {
    if (modalType === "SUBMISSION_PREVIEW" && modalProps?.submission?.id) {
      const openId = String(modalProps.submission.id);
      if (searchParams.get("submission") !== openId) {
        const next = new URLSearchParams(searchParams);
        next.set("submission", openId);
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalType, modalProps]);

  // -----------------------------------------
  // 🔗 Keep the URL clean: strip ?submission when the modal closes
  // -----------------------------------------
  useEffect(() => {
    if (modalType === null && searchParams.get("submission")) {
      const next = new URLSearchParams(searchParams);
      next.delete("submission");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalType]);

  // -----------------------------------------
  // Loading / not found states
  // -----------------------------------------
  if (loading) {
    return <p className="text-center py-10">Loading...</p>;
  }

  if (!competition) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-center px-6 bg-stone-brown">
        <h1 className="text-3xl font-bold text-imperial-red mb-4">
          Competition Not Found
        </h1>
        <p className="text-swan text-lg mb-6">
          The competition you're looking for doesn't exist or may have been
          removed.
        </p>
        <Button variant="secondary">
          <Link to="/competitions">Back to All Competitions</Link>
        </Button>
      </div>
    );
  }

  // -----------------------------------------
  // Main render
  // -----------------------------------------
  return (
    <IndividualCompLayout
      onParticipate={() =>
        openModal("COMP_ENTRY_FORM", {
          comp_id: competition.id,
          competition,
          updateSubmissions,
        })
      }
    >
      <div className="space-y-8 pt-[4.25rem] pb-10">
        <CompetitionDetails competition={competition} />

        <SubmissionsGrid
          submissions={submissions}
          deadline={competition.end_date}
          competition={competition}
          onDelete={handleDeleteSubmission}
        />
      </div>
    </IndividualCompLayout>
  );
};

export default IndividualCompPage;